import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar, User, MapPin, Hash, HelpCircle } from 'lucide-react';
import TruckDetailsForm from './VehicleAssignmentsForm';
import apiClient from '../services/api';

const VIOLET = '#667eea';

const VehicleAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) { console.error('Error parsing session data:', error); return null; }
  };
  const session = getSessionData();

  useEffect(() => { loadAssignments(); }, []);

  const loadAssignments = async () => {
    setLoading(true); setError('');
    try {
      const companyId = session?.companyid;
      const response = await apiClient.get('/VehicleAssignments.php', { params: { companyid: companyId } });
      if (response.data.success) setAssignments(response.data.data || []);
      else throw new Error(response.data.message || 'Failed to load');
    } catch (err) {
      console.error('Error loading assignments:', err);
      let errorMessage = 'Failed to load assignments';
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          setTimeout(() => { window.location.href = '/login'; }, 2000);
        } else errorMessage = err.response.data?.message || errorMessage;
      } else if (err.request) errorMessage = 'Server not responding. Please check if backend is running.';
      else errorMessage = err.message;
      setError(errorMessage);
    } finally { setLoading(false); }
  };

  const handleNew = () => { setEditData(null); setShowForm(true); };
  const handleEdit = (assignment) => { setEditData(assignment); setShowForm(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    try {
      const response = await apiClient.delete(`/VehicleAssignments.php?id=${id}`);
      if (response.data.success) { alert('Assignment deleted successfully!'); loadAssignments(); }
      else alert('Error: ' + response.data.message);
    } catch (err) { alert('Error deleting assignment: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSave = async (formData) => {
    try {
      const isEdit = formData.AssignmentID;
      let response;
      if (isEdit) response = await apiClient.put(`/VehicleAssignments.php?id=${formData.AssignmentID}`, formData);
      else response = await apiClient.post('/VehicleAssignments.php', formData);
      if (response.data.success) {
        alert(isEdit ? 'Assignment updated successfully!' : 'Assignment created successfully!');
        setShowForm(false); setEditData(null); loadAssignments();
      } else throw new Error(response.data.message);
    } catch (err) { throw new Error(err.response?.data?.message || err.message); }
  };

  const handleClose = () => { setShowForm(false); setEditData(null); };

  const inpCls = 'flex-1 border-0 outline-none ml-2 text-sm text-gray-800 placeholder:text-gray-400';

  if (!session?.companyid) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <h3 className="m-0 text-lg font-semibold text-gray-700">No Session Context</h3>
          <p className="m-0 text-sm text-gray-500 max-w-[400px]">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredAssignments = assignments.filter(a =>
    a.TripDocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.VehicleNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.DriverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.OriginCity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.DestinationCity?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusCls = (status) => {
    switch (status) {
      case 'Assigned':    return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-amber-100 text-amber-800';
      case 'Completed':   return 'bg-emerald-100 text-emerald-800';
      case 'Cancelled':   return 'bg-red-100 text-red-800';
      default:            return 'bg-gray-100 text-gray-500';
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ background:'linear-gradient(135deg,#f59e0b,#d97706)' }}>
            <Truck size={28}/>
          </div>
          <div>
            <h1 className="m-0 text-2xl font-bold text-gray-800">Truck Details &amp; Assignments</h1>
            <p className="mt-1 mb-0 text-sm text-gray-500">Manage daily vehicle and driver assignments</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHelp(true)} title="How this page works"
            className="flex items-center gap-2 text-gray-700 bg-white border border-gray-300 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all duration-200">
            <HelpCircle size={18}/>
          </button>
          <button onClick={handleNew}
            className="flex items-center gap-2 text-white border-0 px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer shadow-[0_2px_4px_rgba(102,126,234,0.3)] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(102,126,234,0.4)] transition-all duration-200"
            style={{ background:'linear-gradient(135deg,#667eea,#764ba2)' }}>
            <Plus size={20}/> New Assignment
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center mb-6 gap-4">
        <div className="flex-1 max-w-[500px] flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2.5 transition-all duration-200 focus-within:border-violet-400 focus-within:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]">
          <Search size={20} className="text-gray-400 flex-shrink-0"/>
          <input type="text" placeholder="Search by doc no, vehicle, driver, city..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} className={inpCls}/>
          {searchTerm&&<button onClick={()=>setSearchTerm('')} className="bg-gray-100 border-0 p-1 rounded cursor-pointer flex items-center justify-center hover:bg-gray-200 transition-all duration-200"><X size={16}/></button>}
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
          <span className="text-white px-2.5 py-1 rounded-xl text-[13px] font-semibold" style={{ background:'#f59e0b' }}>{filteredAssignments.length}</span>
          {filteredAssignments.length === 1 ? 'assignment' : 'assignments'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-10 h-10 border-4 border-gray-200 rounded-full" style={{ borderTopColor:VIOLET, animation:'spin 0.8s linear infinite' }}/>
          <p className="text-gray-500 text-sm">Loading assignments...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-5">
          <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-lg text-sm"><strong>Error:</strong> {error}</div>
          <button onClick={loadAssignments} className="flex items-center gap-2 text-white border-0 px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer" style={{ background:'linear-gradient(135deg,#667eea,#764ba2)' }}>Retry</button>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <Truck size={48} className="opacity-30"/>
          <h3 className="m-0 text-lg font-semibold text-gray-700">No assignments found</h3>
          <p className="m-0 text-sm text-gray-500 max-w-[400px]">{searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first assignment'}</p>
          {!searchTerm&&<button onClick={handleNew} className="flex items-center gap-2 text-white border-0 px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer" style={{ background:'linear-gradient(135deg,#667eea,#764ba2)' }}><Plus size={20}/> New Assignment</button>}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto overflow-y-auto shadow-sm" style={{ maxHeight:'calc(100vh - 280px)' }}>
          <table className="w-full border-separate border-spacing-0 text-sm" style={{ tableLayout:'fixed', minWidth:900 }}>
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                {['Trip Doc No','Date','Vehicle','Driver','Origin → Destination','Departure','Arrival','Distance (KM)','Status','Actions'].map((h,i)=>(
                  <th key={h} className={`sticky top-0 z-10 bg-gray-50 border-b-2 border-gray-200 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide ${i===9?'text-right w-[160px]':''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map(assignment => (
                <tr key={assignment.AssignmentID} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <div className="flex items-center gap-1.5">
                      <Hash size={13} style={{ color:'#d97706', flexShrink:0 }}/>
                      <span className="font-mono font-bold text-[0.9rem] tracking-wide" style={{ color:'#d97706' }}>{assignment.TripDocNo || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <div className="flex items-center gap-1.5 text-gray-500"><Calendar size={14} className="text-gray-400"/>{new Date(assignment.AssignmentDate).toLocaleDateString()}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <div className="flex items-start gap-2"><Truck size={14} className="text-gray-400 mt-0.5 flex-shrink-0"/><div><div className="font-medium text-gray-800">{assignment.VehicleNo || '-'}</div><div className="text-[12px] text-gray-400">{assignment.VehicleType || ''}</div></div></div>
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <div className="flex items-start gap-2"><User size={14} className="text-gray-400 mt-0.5 flex-shrink-0"/><div><div className="font-medium text-gray-800">{assignment.DriverName || '-'}</div><div className="text-[12px] text-gray-400">{assignment.DriverPhone || ''}</div></div></div>
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <div className="text-[13px] text-gray-500 whitespace-nowrap">
                      <div className="flex items-center gap-1"><MapPin size={12} style={{ color:'#10b981' }}/>{assignment.OriginCity || 'N/A'}</div>
                      <div className="flex items-center gap-1"><MapPin size={12} style={{ color:'#ef4444' }}/>{assignment.DestinationCity || 'N/A'}</div>
                      {assignment.Route&&<div className="text-[0.82rem] text-gray-500 mt-0.5">Via: {assignment.Route}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">{assignment.DepartureTime || '-'}</td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">{assignment.ArrivalTime || '-'}</td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    {assignment.Odometer_Start && assignment.Odometer_End ? (
                      <div className="font-mono font-semibold">{(parseFloat(assignment.Odometer_End) - parseFloat(assignment.Odometer_Start)).toFixed(2)}</div>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 border-b border-gray-200">
                    <span className={`inline-block px-3 py-1 rounded-xl text-xs font-semibold uppercase tracking-wide ${getStatusCls(assignment.Status)}`}>{assignment.Status || 'Assigned'}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap border-b border-gray-200 w-[160px]">
                    <button onClick={()=>handleEdit(assignment)} title="Edit" className="bg-transparent border border-gray-200 p-1.5 rounded-md cursor-pointer inline-flex items-center justify-center ml-1 text-blue-500 hover:bg-blue-50 hover:border-blue-500 hover:-translate-y-px hover:shadow-sm transition-all duration-200"><Edit2 size={16}/></button>
                    <button onClick={()=>handleDelete(assignment.AssignmentID)} title="Delete" className="bg-transparent border border-gray-200 p-1.5 rounded-md cursor-pointer inline-flex items-center justify-center ml-1 text-red-600 hover:bg-red-50 hover:border-red-600 hover:-translate-y-px hover:shadow-sm transition-all duration-200"><Trash2 size={16}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm&&<TruckDetailsForm onClose={handleClose} onSave={handleSave} editData={editData}/>}

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0" style={{ color: '#fff' }}>
                <HelpCircle size={18} /> How to Use Truck Details &amp; Assignments
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
                style={{ color: '#fff' }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'Create an Assignment', desc: 'Click "New Assignment" to assign a vehicle and driver to a trip, with origin, destination, and route details.' },
                { title: 'Search & Filter', desc: 'Use the search box to find assignments by trip doc number, vehicle, driver, or city.' },
                { title: 'Track Distance', desc: 'The Distance (KM) column is calculated automatically from Odometer Start and End readings entered on the assignment.' },
                { title: 'Status', desc: 'Each assignment shows a status badge: Assigned, In Progress, Completed, or Cancelled.' },
                { title: 'Edit or Delete', desc: 'Use the icons in the Actions column to update or remove an assignment.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleAssignments;